import React from 'react';

import PropTypes from 'prop-types';

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';

// Components
import { Link } from 'daaas-ui';
import ContentContainer from './ContentContainer';
import ContentSection from './ContentSection';

const Root = styled('div')(({ theme }) => ({
  '.listStyleTypeDisc': {
    listStyleType: 'disc',
  },
  '.listStyleTypeCircle': {
    listStyleType: 'circle',
  },
  '.listStyleTypeNone': {
    listStyleType: 'none',
  },
  '& .content_container-dialog': {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0),
  },
  '& ol li::marker': {
    fontFamily: '"Noto Sans", sans-serif',
  },
  '.body1': {
    fontSize: theme.typography.body1.fontSize,
  },
  '.body2': {
    fontSize: theme.typography.body2.fontSize,
  },
}));

export default function TermsAndConditionsBody({ isUsedInDialog = false }) {
  const { t } = useTranslation(['termsAndConditions', 'common']);

  return (
    <Root>
      <ContentContainer
        paddingBottom={false}
        classes={`${isUsedInDialog ? 'content_container-dialog' : ''}`}
      >
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section0Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section0Paragraph1')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section0Paragraph2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section0Paragraph3Part1')}
            <Link
              href={`${t('termsAndConditions:section0Paragraph3Part2href')}`}
              target="_blank"
            >
              {t('termsAndConditions:section0Paragraph3Part2')}
            </Link>
            {t('termsAndConditions:section0Paragraph3Part3')}
            <Link
              href={`${t('termsAndConditions:section0Paragraph3Part4href')}`}
              target="_blank"
            >
              {t('termsAndConditions:section0Paragraph3Part4')}
            </Link>
            {t('termsAndConditions:section0Paragraph3Part5')}
            <Link
              href={`${t('termsAndConditions:section0Paragraph3Part6href')}`}
              target="_blank"
            >
              {t('termsAndConditions:section0Paragraph3Part6')}
            </Link>
            {t('termsAndConditions:section0Paragraph3Part7')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section0Paragraph4')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section0Paragraph5')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section1Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph1Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph1Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph2Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph2Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph3Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph3Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph4Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph4Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph5Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph5Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph6Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph6Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph7Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph7Part2')}
            <Box component="span" fontStyle="italic">
              {t('termsAndConditions:section1Paragraph7Part3')}
            </Box>
            {t('termsAndConditions:section1Paragraph7Part4')}
          </Typography>
          <ul className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part5')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part6')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part7')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part8')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part9')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part10')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part11')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part12')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part13')}
              </Typography>
            </li>
          </ul>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section1Paragraph7Part14')}
            <Box component="span" fontStyle="italic">
              {t('termsAndConditions:section1Paragraph7Part15')}
            </Box>
            {t('termsAndConditions:section1Paragraph7Part16')}
          </Typography>
          <ul className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part17')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part18')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part19')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section1Paragraph7Part20')}
              </Typography>
            </li>
          </ul>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph8Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph8Part2')}
            <Link href={`${t('common:hrefStatcanLicence')}`} target="_blank">
              {t('termsAndConditions:section1Paragraph8Part3')}
            </Link>
            {t('termsAndConditions:section1Paragraph8Part4')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph9Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph9Part2')}
            <Box component="span" fontStyle="italic">
              {t('termsAndConditions:section1Paragraph9Part3')}
            </Box>
            {t('termsAndConditions:section1Paragraph9Part4')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph10Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph10Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph11Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph11Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph12Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph12Part2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            <Box component="span" fontWeight="fontWeightBold">
              {t('termsAndConditions:section1Paragraph13Part1')}
            </Box>
            {t('termsAndConditions:section1Paragraph13Part2')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section2Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section2Paragraph1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section2Paragraph2')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section3Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section3Paragraph1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section3Paragraph2Part1')}
              </Typography>
              <ul className="listStyleTypeDisc">
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph2Part2')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph2Part3')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph2Part4')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph2Part5')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph2Part6')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph2Part7')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph2Part8')}
                    <Box component="span" fontStyle="italic">
                      {t('termsAndConditions:section3Paragraph2Part9')}
                    </Box>
                    {t('termsAndConditions:section3Paragraph2Part10')}
                    <Box component="span" fontStyle="italic">
                      {t('termsAndConditions:section3Paragraph2Part11')}
                    </Box>
                    {t('termsAndConditions:section3Paragraph2Part12')}
                  </Typography>
                </li>
              </ul>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section3Paragraph3')}
              </Typography>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section3Paragraph4Part1')}
              </Typography>
              <ul className="listStyleTypeDisc">
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph4Part2')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph4Part3')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph4Part4')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph4Part5')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph4Part6')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph4Part7')}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant={isUsedInDialog ? 'body2' : 'body1'}
                    sx={{ mb: 2 }}
                    color="textPrimary"
                  >
                    {t('termsAndConditions:section3Paragraph4Part8')}
                  </Typography>
                  <ul className="listStyleTypeCircle">
                    <li>
                      <Typography
                        variant={isUsedInDialog ? 'body2' : 'body1'}
                        sx={{ mb: 2 }}
                        color="textPrimary"
                      >
                        {t('termsAndConditions:section3Paragraph4Part9')}
                      </Typography>
                    </li>
                    <li>
                      <Typography
                        variant={isUsedInDialog ? 'body2' : 'body1'}
                        sx={{ mb: 2 }}
                        color="textPrimary"
                      >
                        {t('termsAndConditions:section3Paragraph4Part10')}
                      </Typography>
                    </li>
                    <li>
                      <Typography
                        variant={isUsedInDialog ? 'body2' : 'body1'}
                        sx={{ mb: 2 }}
                        color="textPrimary"
                      >
                        {t('termsAndConditions:section3Paragraph4Part11')}
                      </Typography>
                    </li>
                    <li>
                      <Typography
                        variant={isUsedInDialog ? 'body2' : 'body1'}
                        sx={{ mb: 2 }}
                        color="textPrimary"
                      >
                        {t('termsAndConditions:section3Paragraph4Part12')}
                      </Typography>
                    </li>
                    <li>
                      <Typography
                        variant={isUsedInDialog ? 'body2' : 'body1'}
                        sx={{ mb: 2 }}
                        color="textPrimary"
                      >
                        {t('termsAndConditions:section3Paragraph4Part13')}
                      </Typography>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section3Paragraph5')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section4Heading')}
          </Typography>
          <ul className="listStyleTypeNone">
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section4Paragraph1Part1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section4Paragraph1Part2')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section4Paragraph1Part3')}
              </Typography>
            </li>
          </ul>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section4Paragraph2Part1')}
            <Link
              href={`${t('termsAndConditions:section4Paragraph2Part2href')}`}
              target="_blank"
            >
              {t('termsAndConditions:section4Paragraph2Part2')}
            </Link>
            {t('termsAndConditions:section4Paragraph2Part3')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section5Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section5Paragraph1')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section6Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section6Paragraph1Part1')}
            <Link
              href={`${t('termsAndConditions:section6Paragraph1Part2href')}`}
              target="_blank"
            >
              {t('termsAndConditions:section6Paragraph1Part2')}
            </Link>
            {t('termsAndConditions:section6Paragraph1Part3')}
            <Link
              href={`https://www.statcan.gc.ca${t('common:/urlPrefix')}${t('common:/aboutdas')}`}
              target="_blank"
            >
              {t('termsAndConditions:section6Paragraph1Part4')}
            </Link>
            {t('termsAndConditions:section6Paragraph1Part5')}
            <Link
              href={`${t('termsAndConditions:dasSupportMailTo')}`}
              target="_blank"
            >
              {t('termsAndConditions:section6Paragraph1Part6')}
            </Link>
            {t('termsAndConditions:section6Paragraph1Part7')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section7Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section7Paragraph1')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section8Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section8Paragraph1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section8Paragraph2')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section8Paragraph3')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section9Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section9Paragraph1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section9Paragraph2')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section9Paragraph3')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section10Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section10Paragraph1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section10Paragraph2')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section10Paragraph3')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section11Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section11Paragraph1')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section12Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section12Paragraph1Part1')}
                <Link
                  href={`${t('termsAndConditions:section12Paragraph1Part2href')}`}
                  target="_blank"
                >
                  {t('termsAndConditions:section12Paragraph1Part2')}
                </Link>
                {t('termsAndConditions:section12Paragraph1Part3')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section12Paragraph2Part1')}
                <Link
                  href={`${t('termsAndConditions:section12Paragraph2Part2href')}`}
                  target="_blank"
                >
                  {t('termsAndConditions:section12Paragraph2Part2')}
                </Link>
                {t('termsAndConditions:section12Paragraph2Part3')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section13Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section13Paragraph1Part1')}
                <Box component="span" fontStyle="italic">
                  {t('termsAndConditions:section13Paragraph1Part2')}
                </Box>
                {t('termsAndConditions:section13Paragraph1Part3')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section13Paragraph2Part1')}
                <Box component="span" fontStyle="italic">
                  {t('termsAndConditions:section13Paragraph2Part2')}
                </Box>
                {t('termsAndConditions:section13Paragraph2Part3')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section14Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section14Paragraph1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section14Paragraph2')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section14Paragraph3')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section14Paragraph4')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section15Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section15Paragraph1Part1')}
                <Link
                  href={`${t('common:hrefStatcanLicence')}`}
                  target="_blank"
                >
                  {t('termsAndConditions:section15Paragraph1Part2')}
                </Link>
                {t('termsAndConditions:section15Paragraph1Part3')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section15Paragraph2')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section16Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section16Paragraph1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section16Paragraph2Part1')}
                <Box component="span" fontStyle="italic">
                  {t('termsAndConditions:section16Paragraph2Part2')}
                </Box>
                {t('termsAndConditions:section16Paragraph2Part3')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section16Paragraph3')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section16Paragraph4')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section16Paragraph5')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section17Heading')}
          </Typography>
          <ol className={isUsedInDialog ? 'body2' : 'body1'}>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section17Paragraph1')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section17Paragraph2')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section17Paragraph3Part1')}
                <br />
                {t('termsAndConditions:section17Paragraph3Part2')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section17Paragraph4')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section17Paragraph5')}
              </Typography>
            </li>
            <li>
              <Typography
                variant={isUsedInDialog ? 'body2' : 'body1'}
                sx={{ mb: 2 }}
                color="textPrimary"
              >
                {t('termsAndConditions:section17Paragraph6')}
              </Typography>
            </li>
          </ol>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section18Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section18Paragraph1')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section19Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section19Paragraph1Part1')}
            <Link
              href={`https://www.statcan.gc.ca${t('common:/urlPrefix')}`}
              target="_blank"
            >
              {t('termsAndConditions:section19Paragraph1Part2')}
            </Link>
            {t('termsAndConditions:section19Paragraph1Part3')}
            <br />
            {t('termsAndConditions:section19Paragraph1Part4')}
            <Link href={`${t('common:hrefStatcanStart')}`} target="_blank">
              {t('termsAndConditions:section19Paragraph1Part5')}
            </Link>
            {t('termsAndConditions:section19Paragraph1Part6')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section20Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section20Paragraph1Part1')}
            <Link
              href={`${t('termsAndConditions:dasSupportMailTo')}`}
              target="_blank"
            >
              {t('termsAndConditions:section20Paragraph1Part2')}
            </Link>
            {t('termsAndConditions:section20Paragraph1Part3')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section20Paragraph2')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section20Paragraph3')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography variant={isUsedInDialog ? 'h5' : 'h2'} sx={{ mb: 2 }}>
            {t('termsAndConditions:section21Heading')}
          </Typography>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:section21Paragraph1Part1')}
            <Link
              href={`${t('termsAndConditions:dasSupportMailTo')}`}
              target="_blank"
            >
              {t('termsAndConditions:section21Paragraph1Part2')}
            </Link>
            {t('termsAndConditions:section21Paragraph1Part3')}
          </Typography>
        </ContentSection>
        <ContentSection>
          <Typography
            variant={isUsedInDialog ? 'body2' : 'body1'}
            sx={{ mb: 2 }}
            color="textPrimary"
          >
            {t('termsAndConditions:end')}
          </Typography>
        </ContentSection>
      </ContentContainer>
    </Root>
  );
}

TermsAndConditionsBody.propTypes = {
  isUsedInDialog: PropTypes.bool,
};
